import { useEffect, useRef } from "react"
import { useListData } from "react-stately"

import { MultiSelect } from "@/components/ui/multi-select"
import type { SelectItemType } from "@/components/ui/select"

import CALD from "./cald.json"

interface ICALDMultiselect {
  value: string[]
  onChange: (value: string[]) => void
  isDisabled?: boolean
}

const CALDMultiselect: React.FC<ICALDMultiselect> = ({
  value = [],
  onChange,
  isDisabled,
}) => {
  const selectedItems = useListData<SelectItemType>({
    initialItems: CALD.filter((item) => value?.includes(item.id)),
  })

  // Keep useListData in sync when value prop changes externally (e.g. form reset)
  const prevValueRef = useRef(value)
  useEffect(() => {
    if (prevValueRef.current === value) return
    prevValueRef.current = value

    const currentIds = new Set(selectedItems.items.map((i) => i.id as string))
    const nextIds = new Set(value)

    // Remove items no longer in value
    for (const id of currentIds) {
      if (!nextIds.has(id)) selectedItems.remove(id)
    }
    // Add items newly in value
    for (const id of nextIds) {
      if (!currentIds.has(id)) {
        const item = CALD.find((c) => c.id === id)
        if (item) selectedItems.append(item)
      }
    }
  }, [value, selectedItems])

  return (
    <MultiSelect
      selectedItems={{
        ...selectedItems,
        append: (...args: Parameters<typeof selectedItems.append>) => {
          selectedItems.append(...args)
          onChange([...value, ...args.map((a) => a.id as string)])
        },
        remove: (...args: Parameters<typeof selectedItems.remove>) => {
          selectedItems.remove(...args)
          onChange(value.filter((v) => !args.includes(v)))
        },
      }}
      label="CALD Representation"
      placeholder="Search"
      items={CALD}
      isDisabled={isDisabled}
    >
      {(item) => <MultiSelect.Item id={item.id}>{item.label}</MultiSelect.Item>}
    </MultiSelect>
  )
}

export default CALDMultiselect
